<template>
  <v-btn
    :href="href"
    :to="to"
    :target="resolvedTarget"
    :rel="resolvedTarget === '_blank' ? 'noopener noreferrer' : undefined"
    :variant="depressed ? 'flat' : undefined"
    :class="[
      'base-button',
      `base-button--${variant}`,
      `base-button--${size}`,
      {
        'btn-wrap': wrap,
        'base-button--block-mobile': blockMobile
      }
    ]"
  >
    <slot>{{ text }}</slot>
    <svg
      v-if="arrow"
      class="base-button__arrow"
      viewBox="0 0 15 15"
      fill="none"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M1 7.5h13M11.5 5 14 7.5l-2.5 2.5" />
    </svg>
  </v-btn>
</template>

<script setup lang="ts">
// TODO: change to WiosnaUI button
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

const props = withDefaults(
  defineProps<{
    text?: string
    /**
     * Design System, sekcja 04 - Przyciski:
     * - primary: czerwony, zaokrąglony (główna akcja konwersji)
     * - secondary: biały, czarny napis i czarna obwódka
     * - ap: wariant Akademii Przyszłości (granatowy)
     */
    variant?: 'primary' | 'secondary' | 'ap'
    /**
     * Rozmiar - jedyne miejsce, w którym ustala się wysokość przycisku.
     * Przyciski w jednym rzędzie powinny mieć ten sam `size`.
     */
    size?: 'small' | 'normal' | 'large'
    href?: string
    to?: RouteLocationRaw
    /** Domyślnie `_blank` dla linków zewnętrznych (`href`), brak dla `to` i zwykłych przycisków. */
    target?: string
    depressed?: boolean
    wrap?: boolean
    /** Strzałka → po etykiecie (jak w Design Systemie). Rysowana jako SVG, bo Poppins nie ma glifu strzałki. */
    arrow?: boolean
    /** Na urządzeniach poniżej breakpointu `md` przycisk zajmuje całą szerokość. */
    blockMobile?: boolean
  }>(),
  {
    text: '',
    variant: 'primary',
    size: 'normal',
    href: undefined,
    to: undefined,
    target: undefined,
    depressed: true,
    wrap: true,
    blockMobile: false,
    arrow: false
  }
)

const resolvedTarget = computed(() => props.target ?? (props.href ? '_blank' : undefined))
</script>

<style lang="scss">
// Kolory i font pochodzą z globalnych tokenów (`:root` w app.scss, źródło: `$color-list` w variables.scss)
.base-button {
  // Rozmiar domyślny (normal); warianty small/large nadpisują tylko te zmienne
  --base-button-height: 48px;
  --base-button-padding-x: 26px;
  --base-button-font-size: 15px;

  // Specyficzność `.v-btn.base-button` przebija style wariantu Vuetify (flat/elevated)
  &.v-btn {
    min-height: var(--base-button-height);
    padding: 8px var(--base-button-padding-x) !important;
    border-radius: 999px;
    border: 1.5px solid transparent;
    box-shadow: none;
    font-family: var(--font-body);
    font-size: var(--base-button-font-size) !important;
    font-weight: 600;
    letter-spacing: -0.01em;

    // `.v-btn` kompensuje własny letter-spacing wcięciem - przy naszym tracking przesuwałoby etykietę
    text-indent: 0;
    line-height: 1.3;
    text-transform: none;
    text-decoration: none;
    transition:
      background-color 0.15s,
      color 0.15s,
      transform 0.1s,
      box-shadow 0.15s;

    &:focus-visible {
      outline: 3px solid var(--sz-focus-blue);
      outline-offset: 3px;
    }

    &:active {
      transform: scale(0.97);
      box-shadow: none;
    }

    .v-btn__content {
      gap: 9px;

      // Vuetify daje `nowrap` - bez tego zawijanie działało tylko przez efekt uboczny `text-wrap: balance`
      white-space: normal;
    }

    // Wyłączamy nakładkę hover Vuetify - hover zdefiniowany jest przez design system
    .v-btn__overlay,
    .v-btn__underlay {
      display: none;
    }

    &.v-btn--disabled {
      opacity: 0.4;
    }
  }

  &--small {
    --base-button-height: 36px;
    --base-button-padding-x: 18px;
    --base-button-font-size: 14px;
  }

  &--large {
    --base-button-height: 56px;
    --base-button-padding-x: 32px;
    --base-button-font-size: 18px;
  }

  &--primary.v-btn {
    background-color: var(--sz-red);
    border-color: var(--sz-red);
    color: #fff;

    &:hover {
      background-color: var(--sz-red-hover);
      border-color: var(--sz-red-hover);
      transform: translateY(-2px);
      box-shadow: 0 10px 26px -8px rgb(212 17 30 / 50%);
    }

    &:active {
      transform: scale(0.97);
      box-shadow: none;
    }

    &.v-btn--disabled {
      background-color: var(--sz-red) !important;
      color: #fff;
    }
  }

  &--secondary.v-btn {
    background-color: #fff;
    border-color: var(--sz-ink);
    color: var(--sz-ink);

    &:hover {
      background-color: var(--sz-ink);
      color: #fff;
    }

    &.v-btn--disabled {
      background-color: #fff !important;
      color: var(--sz-ink);
    }
  }

  &--ap.v-btn {
    background-color: var(--ap-blue);
    border-color: var(--ap-blue);
    color: #fff;

    &:hover {
      background-color: var(--ap-blue-hover);
      border-color: var(--ap-blue-hover);
    }

    &.v-btn--disabled {
      background-color: var(--ap-blue) !important;
      color: #fff;
    }
  }

  @media (width < 960px) {
    &--block-mobile.v-btn {
      display: flex;
      width: 100%;
    }
  }
}

.base-button__arrow {
  display: block;
  flex-shrink: 0;
  width: 0.75em;
  height: 0.75em;
  transition: transform 0.2s;
}

.base-button.v-btn:hover .base-button__arrow {
  transform: translateX(3px);
}

.btn-wrap {
  max-width: 100%;
  height: auto !important;
  white-space: break-spaces;
  text-align: center;

  span {
    width: 100%;
    text-wrap: balance;
  }
}
</style>
